/**
 * Mission 35: Admin Survey Results Page
 *
 * Stats cards, per-question breakdown, response table, cross-slice filtering,
 * CSV + Excel export. Follows AdminSupportTickets pattern.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { AdminCampaignService } from '../services/adminCampaignService';

interface SurveyResponse {
  id: string;
  user_id: string;
  email?: string;
  responses: Array<{
    question_index: number;
    question_text: string;
    question_type: string;
    answer: string;
    answer_label: string;
  }>;
  questions_answered: number;
  questions_total: number;
  completed_at: string | null;
  created_at: string;
}

export const AdminSurveyResults: React.FC = () => {
  const { id: campaignId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [results, setResults] = useState<SurveyResponse[]>([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, abandoned: 0, completion_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState<SurveyResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [completedFilter, setCompletedFilter] = useState('');

  const fetchResults = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const data = await AdminCampaignService.getSurveyResults(campaignId);
      setResults(data.responses || []);
      setStats({
        total: data.total || 0,
        completed: data.completed || 0,
        abandoned: data.abandoned || 0,
        completion_rate: data.completion_rate || 0,
      });
    } catch (e) {
      console.error('Failed to fetch survey results:', e);
    }
    setLoading(false);
  }, [campaignId]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const handleExport = async (format: 'csv' | 'xlsx') => {
    if (!campaignId) return;
    try {
      const blob = await AdminCampaignService.exportSurveyResults(campaignId, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survey_${campaignId.slice(0, 8)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    }
  };

  // Per-question aggregation
  const questionStats = aggregateQuestionStats(results);

  // Filter by search + attributes
  const filteredResults = results.filter((r: any) => {
    if (searchQuery) {
      const match = r.responses.some((q: any) =>
        q.answer_label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.answer?.toLowerCase().includes(searchQuery.toLowerCase())
      ) || r.email?.toLowerCase().includes(searchQuery.toLowerCase());
      if (!match) return false;
    }
    if (platformFilter && r.platform !== platformFilter) return false;
    if (tierFilter && r.subscription_tier !== tierFilter) return false;
    if (completedFilter === 'yes' && !r.completed_at) return false;
    if (completedFilter === 'no' && r.completed_at) return false;
    return true;
  });

  const statCards = [
    { label: 'Sent To', value: stats.total, color: 'var(--status-info-text)', bg: 'var(--status-info-bg)' },
    { label: 'Completed', value: stats.completed, color: 'var(--status-success-text)', bg: 'var(--status-success-bg)' },
    { label: 'Abandoned', value: stats.abandoned, color: 'var(--status-warning-text)', bg: 'var(--status-warning-bg)' },
    { label: 'Completion Rate', value: `${(stats.completion_rate * 100).toFixed(1)}%`, color: 'var(--status-purple-text)', bg: 'var(--status-purple-bg)' },
  ];

  return (
    <div style={{ padding: 32 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate(`/admin/campaigns/${campaignId}`)} style={backLinkStyle}>← Back to Campaign</button>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', margin: '8px 0 0' }}>Survey Results</h1>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {statCards.map((card) => (
          <div key={card.label} style={{ padding: 20, borderRadius: 12, background: card.bg, border: '1px solid rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: card.color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: card.color, marginTop: 4 }}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Per-question breakdown */}
      {questionStats.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>Per-Question Breakdown</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            {questionStats.map((qs) => (
              <div key={qs.questionIndex} style={{ padding: 20, background: 'var(--surface-subtle)', borderRadius: 12, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>Q{qs.questionIndex + 1}: {qs.questionText}</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 12 }}>{qs.questionType} — {qs.totalAnswers} responses</div>
                {qs.answers.map((a) => (
                  <div key={a.answer} style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                      <span style={{ color: 'var(--text-strong)' }}>{a.answer}</span>
                      <span style={{ color: 'var(--text-secondary)' }}>{a.count} ({a.percentage}%)</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${a.percentage}%`, background: 'var(--cedar-primary)', borderRadius: 3 }} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cross-slice Filters + Export */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} style={filterSelectStyle}>
          <option value="">All Platforms</option>
          <option value="ios">iOS</option>
          <option value="android">Android</option>
          <option value="pwa">Web</option>
        </select>
        <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} style={filterSelectStyle}>
          <option value="">All Tiers</option>
          <option value="trial">Trial</option>
          <option value="full">Full</option>
          <option value="extended">Extended</option>
        </select>
        <select value={completedFilter} onChange={(e) => setCompletedFilter(e.target.value)} style={filterSelectStyle}>
          <option value="">All Responses</option>
          <option value="yes">Completed</option>
          <option value="no">Abandoned</option>
        </select>
        <input
          type="text"
          placeholder="Search answers..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-strong)', fontSize: 13, flex: 1, minWidth: 200 }}
        />
        <button onClick={() => handleExport('csv')} style={exportButtonStyle}>Export CSV</button>
        <button onClick={() => handleExport('xlsx')} style={exportButtonStyle}>Export Excel</button>
      </div>
      {filteredResults.length !== results.length && (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Showing {filteredResults.length} of {results.length} responses
        </div>
      )}

      {/* Response table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>Loading...</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--surface-subtle)', borderBottom: '1px solid var(--border)' }}>
              {['Date', 'User', 'Platform', 'Tier', 'Completed', ...getQuestionHeaders(results)].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredResults.map((r) => (
              <tr
                key={r.id}
                onClick={() => setSelectedResponse(r)}
                style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-subtle)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
              >
                <td style={tdStyle}>{new Date(r.created_at).toLocaleDateString()}</td>
                <td style={tdStyle}>{(r as any).first_name || r.email || r.user_id?.slice(0, 8)}</td>
                <td style={tdStyle}><span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{(r as any).platform || '—'}</span></td>
                <td style={tdStyle}><span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{(r as any).subscription_tier || '—'}</span></td>
                <td style={tdStyle}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 100, fontSize: 12, fontWeight: 600,
                    background: r.completed_at ? 'var(--status-success-bg)' : 'var(--status-warning-bg)',
                    color: r.completed_at ? 'var(--status-success-text)' : 'var(--status-warning-text)',
                  }}>
                    {r.completed_at ? 'Yes' : 'No'}
                  </span>
                </td>
                {r.responses.map((q, qi) => (
                  <td key={qi} style={tdStyle}>
                    <span style={{ fontSize: 13 }}>{q.answer_label || q.answer || '—'}</span>
                  </td>
                ))}
              </tr>
            ))}
            {filteredResults.length === 0 && (
              <tr>
                <td colSpan={10} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-tertiary)', padding: 40 }}>
                  No responses yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Detail panel */}
      {selectedResponse && (
        <div style={overlayStyle} onClick={() => setSelectedResponse(null)}>
          <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Response Detail</h3>
              <button onClick={() => setSelectedResponse(null)} style={{ background: 'none', border: 'none', display: 'flex', cursor: 'pointer', color: 'var(--text-secondary)' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <MetaField label="User" value={selectedResponse.email || selectedResponse.user_id?.slice(0, 8) || '—'} />
              <MetaField label="Date" value={new Date(selectedResponse.created_at).toLocaleString()} />
              <MetaField label="Completed" value={selectedResponse.completed_at ? 'Yes' : 'No'} />
              <MetaField label="Questions" value={`${selectedResponse.questions_answered}/${selectedResponse.questions_total}`} />
            </div>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 12 }}>Answers</h4>
            {selectedResponse.responses.map((q, i) => (
              <div key={i} style={{ padding: 12, background: 'var(--surface-subtle)', borderRadius: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>Q{q.question_index + 1}: {q.question_text}</div>
                <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>{q.answer_label || q.answer}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>Type: {q.question_type}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helpers ──

function MetaField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

function getQuestionHeaders(results: SurveyResponse[]): string[] {
  const maxQ = Math.max(...results.map((r) => r.responses.length), 0);
  return Array.from({ length: maxQ }, (_, i) => `Q${i + 1}`);
}

interface QuestionStat {
  questionIndex: number;
  questionText: string;
  questionType: string;
  totalAnswers: number;
  answers: Array<{ answer: string; count: number; percentage: number }>;
}

function aggregateQuestionStats(results: SurveyResponse[]): QuestionStat[] {
  const stats: Record<number, { text: string; type: string; counts: Record<string, number> }> = {};

  for (const r of results) {
    for (const q of r.responses) {
      if (!stats[q.question_index]) {
        stats[q.question_index] = { text: q.question_text, type: q.question_type, counts: {} };
      }
      const label = q.answer_label || q.answer;
      stats[q.question_index].counts[label] = (stats[q.question_index].counts[label] || 0) + 1;
    }
  }

  return Object.entries(stats)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([idx, data]) => {
      const total = Object.values(data.counts).reduce((s, c) => s + c, 0);
      return {
        questionIndex: Number(idx),
        questionText: data.text,
        questionType: data.type,
        totalAnswers: total,
        answers: Object.entries(data.counts)
          .sort(([, a], [, b]) => b - a)
          .map(([answer, count]) => ({
            answer,
            count,
            percentage: Math.round((count / total) * 100),
          })),
      };
    });
}

// ── Styles ──

const thStyle: React.CSSProperties = {
  padding: '10px 16px', textAlign: 'left', fontSize: 12,
  fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
};

const tdStyle: React.CSSProperties = { padding: '12px 16px', verticalAlign: 'top' };

const backLinkStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', padding: 0,
};

const filterSelectStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--border-strong)',
  fontSize: 13, color: 'var(--text-strong)', background: '#fff',
};

const exportButtonStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 8, border: '1.5px solid var(--border-strong)',
  background: '#fff', color: 'var(--text-strong)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.3)', zIndex: 1000,
};

const panelStyle: React.CSSProperties = {
  position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
  background: '#fff', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
  padding: 32, overflowY: 'auto',
};
